// ─────────────────────────────────────────────────────────────────────────────
// 🔑 DOORLYDESK → DOCUMENTS → "Support amounts" (Hannah, 2026-11-03): the staff to-do list of files whose application
// answers "Yes" on step 3 to "Are you obligated to pay alimony/child support?" with no monthly amount recorded. The
// same definition as the decision hold and the production census (backend utils/supportObligations supportNoAmountSql).
// A row leaves the list once an amount is recorded — here, on the step-6 ability-to-repay card, or as a support
// liability on Financials. Styles: the neighbouring Identity Reviews panel's.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const EFFECT = {
  held_at_submit: { label: 'Not submitted', line: 'The decision waits for the amount at submission.', cls: 'bg-yellow-100 text-yellow-700' },
  held_now: { label: 'Decision held', line: 'The decision waits for the amount.', cls: 'bg-yellow-100 text-yellow-700' },
  decided_kept: { label: 'Decided', line: 'The decision stands. Re-pricing is refused until the amount is recorded.', cls: 'bg-gray-100 text-gray-700' },
};

function RecordAmount({ api, headers, row, onDone }) {
  const [amount, setAmount] = useState('');
  const [months, setMonths] = useState('');
  const [voluntary, setVoluntary] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${api}/api/admin/support-amounts/${row.applicantId}`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amountMonthly: Number(amount), monthsRemaining: months === '' ? null : Number(months), voluntary }) });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || 'Could not record the amount.');
      await onDone();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-end gap-3">
      <label className="text-xs font-medium text-gray-500">Monthly amount
        <input data-testid={`support-amount-input-${row.applicantId}`} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="$0.00" className="mt-1 block w-32 h-9 px-3 rounded-lg border border-gray-200 text-sm" />
      </label>
      <label className="text-xs font-medium text-gray-500">Months it continues
        <input data-testid={`support-months-input-${row.applicantId}`} inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="e.g. 56" className="mt-1 block w-28 h-9 px-3 rounded-lg border border-gray-200 text-sm" />
      </label>
      <label className="flex items-center gap-2 text-xs font-medium text-gray-500 h-9">
        <input type="checkbox" checked={voluntary} onChange={(e) => setVoluntary(e.target.checked)} /> Voluntary (not counted)
      </label>
      <button type="button" data-testid={`support-record-${row.applicantId}`} disabled={busy || !(Number(amount) > 0)} onClick={save}
        className="px-3 py-2 text-sm font-semibold rounded-lg bg-[#3a6ad9] text-white disabled:opacity-50">
        Record the amount
      </button>
      {error && <p className="w-full text-xs text-red-600">{error}</p>}
    </div>
  );
}

export default function SupportAmountsPanel({ api, token, Spinner, EmptyState }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${api}/api/admin/support-amounts`, { headers });
      const b = await r.json();
      if (!r.ok) throw new Error(b.error || 'Could not load the support amounts.');
      setRows(b.rows || []); setError('');
    } catch (e) { setError(e.message); setRows([]); }
  }, [api, headers]);
  useEffect(() => { load(); }, [load]);
  if (rows === null) return <div className="flex justify-center py-10">{Spinner ? <Spinner /> : null}</div>;
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="support-amounts-panel">
        <div className="px-5 py-4 border-b border-gray-50">
          <h3 className="font-semibold text-gray-800">Support amounts ({rows.length})</h3>
          <p className="text-xs text-gray-400 mt-0.5">Borrowers who said on step 3 that they pay alimony or child support, with no monthly amount recorded. Record it here, from the decree or order. Counted in DTI when it continues more than 10 months.</p>
        </div>
        {error && <p className="px-5 py-3 text-xs text-red-600">{error}</p>}
        {rows.length === 0 ? (EmptyState ? <EmptyState message="No support amounts to record." /> : <p className="px-5 py-6 text-sm text-gray-500">No support amounts to record.</p>) : (
          <div className="divide-y divide-gray-50">
            {rows.map((r) => {
              const ef = EFFECT[r.effect] || EFFECT.held_now;
              return (
                <div key={r.applicantId} data-testid={`support-amount-row-${r.applicantId}`} className="px-5 py-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{r.borrowerName || 'Borrower'} <span className="text-xs font-normal text-gray-400">· file #{r.ownerId}{r.loanNumber ? ` · ${r.loanNumber}` : ''}</span></p>
                      {!r.isPrimary && <p className="text-xs text-gray-500">Co-borrower: {r.applicantName || `#${r.applicantId}`}</p>}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs text-gray-500">Status: {String(r.status || '—').replace(/_/g, ' ')}</span>
                        <span className="text-xs text-gray-500">· Decided: {r.decided ? 'Yes' : 'No'}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ef.cls}`}>{ef.label}</span>
                        {!r.real && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">Test</span>}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{ef.line}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Link to={`/admin/borrowers/${r.ownerId}`} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">Open the file</Link>
                      <Link to={`/admin/borrowers/${r.applicantId}?tab=financial_details`} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">Financials</Link>
                    </div>
                  </div>
                  <RecordAmount api={api} headers={headers} row={r} onDone={load} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
